"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import Head from "next/head";
import {
  X,
  Ruler,
  Package,
  RefreshCw,
  Scissors,
  Phone,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
type SizeTab = "tops" | "bottoms";
type CareKey = "Linen" | "Wool" | "Cotton" | "Silk";

interface Fabric {
  id?: number;
  name: string;
  origin: string;
  best: string[];
  tag: string;
  img: string;
  img1: string;
  img2: string;
  img3: string;
  img4: string;
  description?: string;
}

interface Slot {
  id: number;
  slot_type: "fixed" | "custom";
  slot_template_id: string | null;
  template_label: string | null;
  start_time: string;
  end_time: string;
  applies_to: "date" | "always";
  slot_date: string | null;
  capacity: number;
  booked_count: number;
  remaining: number;
  status: "available" | "full" | "blocked";
}

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");

// ─── Fabric category for THIS page ───────────────────────────────────────────
const FABRIC_CATEGORY = "men" as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function toYMD(y: number, mo: number, d: number): string {
  return `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function normalizeYMD(input: string): string {
  return String(input || "").slice(0, 10);
}

function todayYMD(): string {
  const n = new Date();
  return toYMD(n.getFullYear(), n.getMonth(), n.getDate());
}

// ─── useFabrics hook ──────────────────────────────────────────────────────────
function useFabrics(category: string) {
  const [group1, setGroup1]   = useState<Fabric[]>([]);
  const [group2, setGroup2]   = useState<Fabric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tick, setTick]       = useState(0);

  useEffect(() => {
    if (!category) return;
    let cancelled = false;

    async function fetchFabrics() {
      setLoading(true);
      setError("");
      try {
        const url = `${API_BASE}/api/fabrics?category=${encodeURIComponent(category)}`;
        const res = await fetch(url);
        const ct  = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error(`Server error (${res.status})`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load fabrics");
        if (cancelled) return;
        const catData = data.grouped?.[category] ?? {};
        setGroup1(catData[1] ?? catData["1"] ?? []);
        setGroup2(catData[2] ?? catData["2"] ?? []);
      } catch (e: unknown) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load fabrics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchFabrics();
    return () => { cancelled = true; };
  }, [category, tick]);

  return { group1, group2, loading, error, refetch: () => setTick((t) => t + 1) };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner({ size = 16, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="animate-spin" style={{ color }}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── Fabric Skeleton ──────────────────────────────────────────────────────────
function FabricCarouselSkeleton() {
  return (
    <div className="cr-outer pt-2 sm:pt-6 py-12 sm:py-16 md:py-20 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-pulse">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl overflow-hidden border-2 border-neutral-100">
            <div className="h-[180px] bg-neutral-100" />
            <div className="p-4 bg-[#f8f5ef]">
              <div className="h-4 bg-neutral-200 rounded mb-2 w-3/4" />
              <div className="h-3 bg-neutral-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlotSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[64px] bg-neutral-100 rounded-2xl border border-neutral-200" />
      ))}
    </div>
  );
}

// ─── Slot List ────────────────────────────────────────────────────────────────
function SlotList({
  date,
  selectedSlotId,
  onSelect,
}: {
  date: string;
  selectedSlotId: number | null;
  onSelect: (id: number) => void;
}) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doFetch = useCallback(async (d: string) => {
    setLoading(true);
    setError("");
    setSlots([]);
    try {
      const ymd = normalizeYMD(d);
      const res = await fetch(`${API_BASE}/api/appointments/slots/available?date=${encodeURIComponent(ymd)}`);
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error(`Server error (${res.status})`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load slots");
      setSlots(data.data || []);
    } catch (e: any) {
      setError(e.message || "Could not load slots");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (date) doFetch(date);
    else { setSlots([]); setError(""); }
  }, [date, doFetch]);

  if (!date) return null;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-semibold text-neutral-700 uppercase tracking-[0.18em]">Available Slots</p>
        {!loading && slots.length > 0 && (
          <span className="text-xs text-neutral-400">{slots.length} slot{slots.length !== 1 ? "s" : ""}</span>
        )}
      </div>
      {loading && <SlotSkeleton />}
      {!loading && error && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          <span>⚠ {error}</span>
          <button type="button" onClick={() => doFetch(date)} className="font-semibold underline">Retry</button>
        </div>
      )}
      {!loading && !error && slots.length === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-center">
          <p className="text-sm font-semibold text-amber-700">No slots on this date</p>
          <p className="text-xs text-amber-600 mt-1">Pick another available date.</p>
        </div>
      )}
      {!loading && !error && slots.map((slot) => {
        const isSelected = selectedSlotId === slot.id;
        const left = slot.remaining ?? slot.capacity - slot.booked_count;
        const hot = left <= 2;
        return (
          <button
            key={slot.id}
            type="button"
            onClick={() => onSelect(slot.id)}
            className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-150 ${
              isSelected
                ? "border-[#C7AB5F] bg-[#fffaf2] ring-2 ring-[#C7AB5F]/20 shadow-sm"
                : "border-neutral-200 bg-white hover:border-[#C7AB5F] hover:bg-[#fffdf8]"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-[#1a1a1a]">
                  {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                </p>
                {slot.template_label && <p className="mt-0.5 text-xs text-neutral-500">{slot.template_label}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${hot ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                  {left === 1 ? "Last spot!" : `${left} left`}
                </span>
                {isSelected && (
                  <span className="w-5 h-5 rounded-full bg-[#C7AB5F] flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.3 2.3L8 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Mini Calendar Picker ─────────────────────────────────────────────────────
function CalendarPicker({ value, onChange }: { value: string; onChange: (ymd: string) => void }) {
  const TODAY = todayYMD();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [loadingMonth, setLoadingMonth] = useState(false);

  const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAY_NAMES = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const scanMonth = useCallback(async (year: number, month: number) => {
    setLoadingMonth(true);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const found = new Set<string>();
    const promises = Array.from({ length: daysInMonth }, (_, i) => {
      const d = toYMD(year, month, i + 1);
      if (d < TODAY) return Promise.resolve({ d, count: 0 });
      return fetch(`${API_BASE}/api/appointments/slots/available?date=${encodeURIComponent(d)}`)
        .then((r) => r.json())
        .then((data) => ({ d, count: data.data?.length ?? 0 }))
        .catch(() => ({ d, count: 0 }));
    });
    const results = await Promise.all(promises);
    results.forEach(({ d, count }) => { if (count > 0) found.add(d); });
    setDatesWithSlots(found);
    setLoadingMonth(false);
  }, [TODAY]);

  useEffect(() => { scanMonth(viewYear, viewMonth); }, [viewYear, viewMonth, scanMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
        <button type="button" onClick={prevMonth} disabled={isCurrentMonth}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100 transition disabled:opacity-30">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold text-[#1a1a1a]">{MONTH_NAMES[viewMonth]} {viewYear}</p>
          {loadingMonth
            ? <p className="text-[10px] text-[#C7AB5F] mt-0.5 flex items-center justify-center gap-1"><Spinner size={9} color="#C7AB5F" /> checking…</p>
            : <p className="text-[10px] text-neutral-400 mt-0.5">{datesWithSlots.size} date{datesWithSlots.size !== 1 ? "s" : ""} available</p>
          }
        </div>
        <button type="button" onClick={nextMonth}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100 transition">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <div className="grid grid-cols-7 px-2 pt-2 pb-1">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-neutral-400 uppercase py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 px-2 pb-3 gap-y-1">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const ymd = toYMD(viewYear, viewMonth, day);
          const isPast = ymd < TODAY;
          const isToday = ymd === TODAY;
          const isSelected = ymd === value;
          const hasSlots = datesWithSlots.has(ymd);
          return (
            <button key={day} type="button"
              disabled={isPast || (!hasSlots && !loadingMonth)}
              onClick={() => onChange(ymd)}
              className={`relative h-8 w-full rounded-lg text-xs font-medium transition-all ${
                isPast ? "text-neutral-200 cursor-not-allowed"
                  : isSelected ? "bg-[#C7AB5F] text-white shadow-sm"
                  : isToday && hasSlots ? "bg-[#fffaf2] text-[#1a1a1a] ring-1 ring-[#C7AB5F]"
                  : hasSlots ? "text-[#1a1a1a] hover:bg-[#fffaf2] hover:ring-1 hover:ring-[#C7AB5F]"
                  : loadingMonth ? "text-neutral-300 cursor-wait"
                  : "text-neutral-300 cursor-not-allowed"
              }`}
            >
              {day}
              {hasSlots && !isSelected && !isPast && (
                <span className="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-[4px] h-[4px] rounded-full bg-[#C7AB5F]" />
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-t border-neutral-100 bg-neutral-50">
        <span className="flex items-center gap-1 text-[10px] text-neutral-500">
          <span className="w-[5px] h-[5px] rounded-full bg-[#C7AB5F] inline-block" /> Slots available
        </span>
        <span className="flex items-center gap-1 text-[10px] text-neutral-500">
          <span className="w-[5px] h-[5px] rounded-full bg-neutral-300 inline-block" /> No slots
        </span>
      </div>
    </div>
  );
}

function onlyLetters(value: string) {
  return value
    .replace(/[^a-zA-Z ]/g, "")
    .replace(/\s+/g, " ")
    .replace(/^\s+/, "");
}

function onlyNumbers(value: string) {
  return value.replace(/\D/g, "").slice(0, 10);
}

function cleanAddress(value: string) {
  return value.replace(/[^a-zA-Z0-9\s,./#()-]/g, "");
}

function validName(value: string) {
  return /^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(value.trim());
}

function validPhone(value: string) {
  return /^[0-9]{10}$/.test(value);
}

function validEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// ─── Fabric Appointment Popup ─────────────────────────────────────────────────
function FabricAppointmentPopup({
  fabric,
  onClose,
}: {
  fabric: Fabric;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    date: "",
    description: "",
  });
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDateChange = (ymd: string) => {
    setFormData((p) => ({ ...p, date: normalizeYMD(ymd) }));
    setSelectedSlotId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (!selectedSlotId) { setError("Please select a time slot."); return; }
    if (!formData.date) { setError("Please select a date."); return; }

    if (!validName(formData.name)) {
    setError("Please enter a valid full name.");
    return;
    }

    if (!validPhone(formData.phone)) {
        setError("Phone number must be exactly 10 digits.");
        return;
    }

    if (!validEmail(formData.email)) {
        setError("Please enter a valid email address.");
        return;
    }

    if (formData.address.trim().length < 10) {
        setError("Please enter your complete address.");
        return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/fabric-appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: selectedSlotId,
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          address: formData.address.trim(),
          date: normalizeYMD(formData.date),
          description: formData.description.trim(),
          fabric_name: fabric.name,
          fabric_origin: fabric.origin,
          fabric_tag: fabric.tag,
          fabric_best_for: fabric.best.join(", "),
        }),
      });

      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json")
        ? await res.json()
        : { message: `Server error (${res.status})` };

      if (!res.ok) { setError(data.message || "Something went wrong."); return; }
      setIsBooked(true);
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[80] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full py-24 max-w-2xl bg-white rounded-3xl shadow-2xl border border-zinc-200 overflow-hidden">
            {/* Header */}
            <div className="bg-[#1a1a1a] px-6 py-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-['DM_Mono'] text-[10px] tracking-[0.28em] uppercase text-[#C7AB5F] mb-1">Book Appointment</p>
                <h3 className="font-['Cormorant_Garamond'] italic text-2xl text-white leading-tight">{fabric.name}</h3>
                <p className="font-['DM_Mono'] text-[10px] tracking-[0.15em] uppercase text-white/50 mt-1">{fabric.origin}</p>
              </div>
              <button onClick={onClose}
                className="flex-shrink-0 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition">
                <X size={16} className="text-white" />
              </button>
            </div>

            {/* Fabric Summary Strip */}
            <div className="bg-[#f8f5ef] px-6 py-3 flex flex-wrap gap-2 border-b border-zinc-200">
              <span className="bg-[#C7AB5F] text-white text-[9px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full font-['DM_Mono']">
                {fabric.tag}
              </span>
              {fabric.best.slice(0, 3).map((b) => (
                <span key={b} className="bg-white text-zinc-600 text-[9px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full font-['DM_Mono'] border border-zinc-200">
                  {b}
                </span>
              ))}
              {fabric.best.length > 3 && (
                <span className="text-[9px] text-zinc-400 font-['DM_Mono'] uppercase tracking-[0.12em] flex items-center">
                  +{fabric.best.length - 3} more
                </span>
              )}
            </div>

            <div className="px-6 mt-4 flex justify-center">
              <p className="text-sm leading-6 text-neutral-600 max-w-md text-center">
                <span className="font-semibold text-[#1a1a1a]">Note:</span>{" "}
                <span className="text-[#c8a96e] font-medium">Our services are currently available only in the NCR region.</span>
              </p>
            </div>

            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
              {!isBooked ? (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Full Name *</label>
                    <input type="text" required placeholder="Your full name" value={formData.name} maxLength={50} autoComplete="name" pattern="[A-Za-z ]+" title="Only letters are allowed" onChange={(e)=> setFormData({ ...formData, name: onlyLetters(e.target.value) }) }
                      className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-4 text-[14px] text-black outline-none transition focus:border-[#C7AB5F] focus:ring-4 focus:ring-[#C7AB5F]/10 placeholder:text-neutral-400" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Phone *</label>
                      <input type="tel" required placeholder="+91 XXX XXX XXXX" value={formData.phone} maxLength={10} inputMode="numeric" pattern="[0-9]{10}" autoComplete="tel" title="+91 XXX XXX XXXX" onChange={(e)=> setFormData({ ...formData, phone: onlyNumbers(e.target.value) }) }
                        className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-4 text-[14px] text-black outline-none transition focus:border-[#C7AB5F] focus:ring-4 focus:ring-[#C7AB5F]/10 placeholder:text-neutral-400" />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Email *</label>
                      <input type="email" required maxLength={100} autoComplete="email" placeholder="email@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value, }) }
                        className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-4 text-[14px] text-black outline-none transition focus:border-[#C7AB5F] focus:ring-4 focus:ring-[#C7AB5F]/10 placeholder:text-neutral-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Preferred Date *</label>
                    <CalendarPicker value={formData.date} onChange={handleDateChange} />
                    {formData.date && (
                      <SlotList date={formData.date} selectedSlotId={selectedSlotId} onSelect={setSelectedSlotId} />
                    )}
                  </div>
                  <div>
                    <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Address *</label>
                    <input type="text" required maxLength={200} placeholder="Your full address" value={formData.address} onChange={(e)=> setFormData({ ...formData, address:cleanAddress(e.target.value) }) }
                      className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-4 text-[14px] text-black outline-none transition focus:border-[#C7AB5F] focus:ring-4 focus:ring-[#C7AB5F]/10 placeholder:text-neutral-400" />
                  </div>
                  <div>
                    <label className="block mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-600">Additional Notes</label>
                    <textarea rows={3} placeholder="Tell us about your vision or requirements…" value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-[14px] text-black outline-none resize-none transition focus:border-[#C7AB5F] focus:ring-4 focus:ring-[#C7AB5F]/10 placeholder:text-neutral-400" />
                  </div>
                  {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
                  )}
                  <button type="submit"
                    disabled={isLoading || !selectedSlotId || !formData.date}
                    className="w-full h-12 bg-[#051173] text-white text-[12px] font-semibold uppercase tracking-[0.18em] hover:bg-[#C7AB5F] transition rounded-xl disabled:opacity-60 disabled:cursor-not-allowed font-['DM_Mono'] flex items-center justify-center gap-2">
                    {isLoading ? (
                      <><Spinner size={15} color="white" /> Submitting…</>
                    ) : !formData.date ? "Select a Date First"
                      : !selectedSlotId ? "Select a Time Slot"
                      : "Confirm Appointment"}
                  </button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] italic text-2xl text-zinc-900 mb-2">Appointment Confirmed!</h3>
                  <p className="text-zinc-500 text-sm mb-1">
                    A confirmation email with fabric details has been sent to{" "}
                    <span className="font-semibold text-zinc-800">{formData.email}</span>.
                  </p>
                  <p className="text-zinc-400 text-xs mb-6">Please arrive 10 minutes early.</p>
                  <div className="bg-[#f8f5ef] rounded-2xl p-4 text-left mb-6 border border-[#C7AB5F]/20">
                    <p className="font-['DM_Mono'] text-[9px] tracking-[0.2em] uppercase text-[#C7AB5F] mb-2">Selected Fabric</p>
                    <p className="font-['Cormorant_Garamond'] italic text-lg text-zinc-900">{fabric.name}</p>
                    <p className="font-['DM_Mono'] text-[10px] text-zinc-500 uppercase tracking-[0.15em]">{fabric.origin}</p>
                  </div>
                  <button onClick={onClose}
                    className="font-['DM_Mono'] text-[11px] text-[#C7AB5F] uppercase tracking-[0.18em] underline underline-offset-4">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Static Data ──────────────────────────────────────────────────────────────
const pageData = {
  title: "Men",
  img: "/men.png",
  description: "Modern tailoring, relaxed essentials, and premium occasion wear.",
  customization: [
    "Custom sleeve length",
    "Neck and collar styles",
    "Monogram embroidery",
    "Fabric selection",
    "Altered fit: slim, regular, relaxed",
  ],
  SIZE_GUIDE_TOPS: [
    { label: "S",   chest: '36"', shoulder: '16.5"', sleeve: '24.5"' },
    { label: "M",   chest: '38"', shoulder: '17"',   sleeve: '25"'   },
    { label: "L",   chest: '40"', shoulder: '17.5"', sleeve: '25.5"' },
    { label: "XL",  chest: '42"', shoulder: '18"',   sleeve: '26"'   },
    { label: "XXL", chest: '44"', shoulder: '18.5"', sleeve: '26.5"' },
    { label: "3XL", chest: '46"', shoulder: '19"',   sleeve: '27"'   },
  ],
  SIZE_GUIDE_BOTTOMS: [
    { label: "28", waist: '28"', hip: '36"', inseam: '30"' },
    { label: "30", waist: '30"', hip: '38"', inseam: '30"' },
    { label: "32", waist: '32"', hip: '40"', inseam: '31"' },
    { label: "34", waist: '34"', hip: '42"', inseam: '31"' },
    { label: "36", waist: '36"', hip: '44"', inseam: '32"' },
    { label: "38", waist: '38"', hip: '46"', inseam: '32"' },
  ],
};

const relatedSlides = [
  { title: "", img: "/001.jpg" },
  { title: "", img: "/002.jpg" },
  { title: "", img: "/003.png" },
  { title: "", img: "/004.png" },
  { title: "", img: "/002.jpg" },
  { title: "", img: "/004.png" },
  { title: "", img: "/001.jpg" },
  { title: "", img: "/003.png" },
];

const menDressTypes = [
  { name: "Suit Pant",     img: "/Suit Pant.jpg" },
  { name: "Formal Shirt",  img: "/Formal Shirt.jpg" },
  { name: "Blazer",        img: "/Blazer.jpg" },
  { name: "Waistcoat",     img: "/Waistcoat.jpg" },
  { name: "Kurta Set",     img: "/Kurta Set.jpg" },
  { name: "Nehru Jacket",  img: "/Nehru Jacket.jpg" },
  { name: "Pathani Suit",  img: "/Pathani Suit.jpg" },
  { name: "Trousers",      img: "/Trousers.jpg" },
  { name: "Bandhgala",     img: "/Bandhgala.jpg" },
  { name: "Sherwani",      img: "/Sherwani.jpg" },
  { name: "Jodhpuri Suit", img: "/Jodhpuri Suit.jpg" },
  { name: "Casual Jacket", img: "/Casual Jacket.jpg" },
];


const careInstructions: Record<CareKey, string[]> = {
  Linen: [
    "Cold machine wash or hand wash",
    "Do not tumble dry — air flat",
    "Iron while slightly damp on medium heat",
    "Store folded, not hung",
  ],
  Wool: [
    "Dry clean recommended",
    "Hand wash in cold water with wool wash only",
    "Lay flat to dry — never hang wet wool",
    "Cedar blocks ward off moths",
  ],
  Cotton: [
    "Machine wash 30°C gentle cycle",
    "Line dry in shade",
    "Iron on medium-high",
    "Fold neatly — avoid wire hangers",
  ],
  Silk: [
    "Dry clean or hand wash in cold water",
    "Never wring — press in a towel",
    "Iron on low with a pressing cloth",
    "Store in a breathable garment bag",
  ],
};

const doorstepServiceSteps = [
  { title: "Schedule a Visit",              description: "Contact us to schedule a convenient time for our tailor to visit your home at your preferred date and time." },
  { title: "Consultation and Measurements", description: "Our expert tailor will consult with you to understand your requirements and take accurate measurements." },
  { title: "Fabric Selection",              description: "If you have your own fabric, our tailor will inspect it to ensure it's suitable for your desired design." },
  { title: "Stitching and Fittings",        description: "Once the fabric and measurements are finalized, our team gets to work on creating your custom garment." },
  { title: "Final Delivery",                description: "After the fitting and any adjustments, your finished garment is delivered to your doorstep." },
];

const SLIDES_PER_VIEW_HERO = 4;

// ─── Fabric Carousel ──────────────────────────────────────────────────────────
function FabricCarousel({
  fabricList,
  onOpen,
}: {
  fabricList: Fabric[];
  onOpen: (f: Fabric) => void;
}) {
  const [current, setCurrent]       = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDelta, setDragDelta]   = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const autoRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const [spv, setSpv] = useState(4);

  useEffect(() => {
    const upd = () => {
      const w = window.innerWidth;
      setSpv(w < 640 ? 1 : w < 768 ? 2 : w < 1024 ? 3 : 4);
    };
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  const maxIdx = Math.max(0, fabricList.length - spv);
  const goTo = useCallback((idx: number) => { setCurrent(Math.max(0, Math.min(idx, maxIdx))); }, [maxIdx]);
  const next = useCallback(() => goTo(current >= maxIdx ? 0 : current + 1), [current, maxIdx, goTo]);
  const prev = useCallback(() => goTo(current <= 0 ? maxIdx : current - 1), [current, maxIdx, goTo]);

  const resetAuto = () => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(next, 4500);
  };

  useEffect(() => {
    autoRef.current = setInterval(next, 4500);
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [next]);

  const onDragStart = (x: number) => { setIsDragging(true); setDragStartX(x); setDragDelta(0); if (autoRef.current) clearInterval(autoRef.current); };
  const onDragMove  = (x: number) => { if (isDragging) setDragDelta(x - dragStartX); };
  const onDragEnd   = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragDelta < -60) next(); else if (dragDelta > 60) prev();
    setDragDelta(0);
    resetAuto();
  };

  const slideW     = trackRef.current ? trackRef.current.offsetWidth / spv : 0;
  const translateX = -(current * slideW) + dragDelta;

  return (
    <>
      <style>{`
        .cr-outer   { position: relative; padding-bottom: 2.5rem; }
        .cr-wrap    { overflow: hidden; border-radius: 16px; cursor: grab; user-select: none; }
        .cr-wrap:active { cursor: grabbing; }
        .cr-track   { display: flex; transition: transform .5s cubic-bezier(.25,.46,.45,.94); will-change: transform; }
        .cr-track.dragging { transition: none; }
        .cr-slide   { flex-shrink: 0; padding: 0 8px; box-sizing: border-box; }
        .cr-card    { background: #f8f5ef; border-radius: 16px; border: 2px solid #f0ece3; overflow: hidden; transition: border-color .3s,box-shadow .3s,transform .3s; cursor: pointer; display: flex; flex-direction: column; }
        .cr-card:hover { border-color: #C7AB5F; box-shadow: 0 12px 36px rgba(199,171,95,.18); transform: translateY(-4px); }
        .cr-img-wrap { height: 180px; overflow: hidden; position: relative; }
        .cr-img     { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; pointer-events: none; }
        .cr-card:hover .cr-img { transform: scale(1.08); }
        .cr-tag     { position: absolute; top: 10px; left: 10px; background: #C7AB5F; color: #fff; font-family: 'DM Mono',monospace; font-size: 9px; letter-spacing: .18em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px; }
        .cr-view    { position: absolute; top: 10px; right: 10px; background: #C7AB5F; color: #fff; font-family: 'DM Mono',monospace; font-size: 9px; letter-spacing: .15em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px; opacity: 0; transition: opacity .2s; }
        .cr-card:hover .cr-view { opacity: 1; }
        .cr-info    { padding: 16px 18px 18px; flex: 1; }
        .cr-name    { font-family: 'Cormorant Garamond',Georgia,serif; font-style: italic; font-size: 1.1rem; color: #18181b; margin-bottom: 4px; line-height: 1.2; }
        .cr-origin  { font-family: 'DM Mono',monospace; font-size: 9.5px; letter-spacing: .2em; text-transform: uppercase; color: #a1a1aa; }
        .cr-nav { position: absolute; top: 50%; transform: translateY(calc(-50% - 1.25rem)); z-index: 10; width: 44px; height: 44px; border-radius: 50%; background: #fff; border: 2px solid #C7AB5F; color: #C7AB5F; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .25s,border-color .25s,color .25s,box-shadow .25s,transform .2s; box-shadow: 0 4px 14px rgba(199,171,95,.22); }
        .cr-nav:hover { background: #C7AB5F; border-color: #C7AB5F; color: #fff; box-shadow: 0 6px 20px rgba(199,171,95,.4); transform: translateY(calc(-50% - 1.25rem)) scale(1.08); }
        .cr-nav:active { transform: translateY(calc(-50% - 1.25rem)) scale(.96); }
        .cr-nav-prev { left: -22px; }
        .cr-nav-next { right: -22px; }
        .cr-dots { display: flex; align-items: center; justify-content: center; gap: 7px; position: absolute; bottom: 0; left: 0; right: 0; }
        .cr-dot  { height: 6px; border-radius: 3px; background: #e4e0d8; transition: width .35s ease,background .35s ease; cursor: pointer; border: none; padding: 0; }
        .cr-dot.on  { background: #C7AB5F; width: 24px !important; }
        .cr-dot:not(.on) { width: 6px; }
        .cr-dot:hover:not(.on) { background: #d4bc78; }
        @media (max-width: 640px) { .cr-nav-prev { left: -14px; } .cr-nav-next { right: -14px; } .cr-nav { width: 38px; height: 38px; } }
      `}</style>

      <div className="cr-outer pt-2 sm:pt-6 py-12 sm:py-16 md:py-20 px-4 sm:px-6 max-w-7xl mx-auto">
        <button className="cr-nav cr-nav-prev" onClick={() => { prev(); resetAuto(); }} aria-label="Previous slide">
          <ChevronLeft size={18} />
        </button>
        <div className="cr-wrap" ref={trackRef}
          onMouseDown={(e) => onDragStart(e.clientX)} onMouseMove={(e) => onDragMove(e.clientX)}
          onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
          onTouchStart={(e) => onDragStart(e.touches[0].clientX)} onTouchMove={(e) => onDragMove(e.touches[0].clientX)}
          onTouchEnd={onDragEnd}
        >
          <div className={`cr-track ${isDragging ? "dragging" : ""}`} style={{ transform: `translateX(${translateX}px)` }}>
            {fabricList.map((fab, idx) => (
              <div key={`${fab.name}-${fab.id ?? idx}`} className="cr-slide" style={{ width: `${100 / spv}%` }}>
                <div className="cr-card" onClick={() => !isDragging && onOpen(fab)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(fab)}>
                  <div className="cr-img-wrap">
                    <img src={fab.img} alt={fab.name} className="cr-img" draggable={false} />
                    {fab.tag && <span className="cr-tag">{fab.tag}</span>}
                    <span className="cr-view">View</span>
                  </div>
                  <div className="cr-info">
                    <p className="cr-name">{fab.name}</p>
                    <p className="cr-origin">{fab.origin}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button className="cr-nav cr-nav-next" onClick={() => { next(); resetAuto(); }} aria-label="Next slide">
          <ChevronRight size={18} />
        </button>
        <div className="cr-dots">
          {Array.from({ length: maxIdx + 1 }).map((_, i) => (
            <button key={i} className={`cr-dot ${i === current ? "on" : ""}`} onClick={() => { goTo(i); resetAuto(); }} aria-label={`Go to slide ${i + 1}`} />
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MenPage() {
  const [showSizeGuide, setShowSizeGuide]         = useState(false);
  const [sizeTab, setSizeTab]                     = useState<SizeTab>("tops");
  const [activeCare, setActiveCare]               = useState<CareKey>("Linen");
  const [currentSlide, setCurrentSlide]           = useState(0);
  const [fabricPopup, setFabricPopup]             = useState<Fabric | null>(null);
  const [selectedImage, setSelectedImage]         = useState("");
  const [appointmentFabric, setAppointmentFabric] = useState<Fabric | null>(null);

  // ── DB fabric data — category = "men" ─────────────────────────────────────
  const {
    group1: fabrics,
    group2: fabrics1,
    loading: fabricsLoading,
    error: fabricsError,
    refetch: refetchFabrics,
  } = useFabrics(FABRIC_CATEGORY);

  const router = useRouter();

  useEffect(() => {
    if (fabricPopup) setSelectedImage(fabricPopup.img);
  }, [fabricPopup]);

  const totalSlides   = Math.ceil(relatedSlides.length / SLIDES_PER_VIEW_HERO);
  const slideStart    = currentSlide * SLIDES_PER_VIEW_HERO;
  const visibleSlides = relatedSlides.slice(slideStart, slideStart + SLIDES_PER_VIEW_HERO);

  const prevSlide = () => setCurrentSlide((p) => (p === 0 ? totalSlides - 1 : p - 1));
  const nextSlide = () => setCurrentSlide((p) => (p + 1) % totalSlides);

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap"
          rel="stylesheet"
        />
      </Head>

      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes slideIn  { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .anim-slide-in { animation: slideIn  0.3s  ease-out forwards; }
        .anim-fade-up  { animation: fadeInUp 0.45s ease    both; }
        .card-hover { transition: transform .3s ease, box-shadow .3s ease; }
        .card-hover:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,.12); }
        .shape-full         { border-radius: 5px; }
        .shape-soft-curve   { border-top-left-radius:36px; border-top-right-radius:36px; border-bottom-left-radius:120px; border-bottom-right-radius:120px; }
        .shape-bottom-curve { border-radius: 5px; }
        .shape-top-curve    { border-top-left-radius:120px; border-top-right-radius:120px; border-bottom-left-radius:18px; border-bottom-right-radius:18px; }
        @media (max-width:767px) {
          .shape-soft-curve   { border-top-left-radius:24px; border-top-right-radius:24px; border-bottom-left-radius:80px; border-bottom-right-radius:80px; }
          .shape-bottom-curve { border-bottom-left-radius:80px; border-bottom-right-radius:80px; }
          .shape-top-curve    { border-top-left-radius:80px; border-top-right-radius:80px; }
        }
      `}</style>

      <main className="bg-white min-h-screen">

        {/* Hero */}
        <section className="relative w-full h-[60vh] sm:h-[65vh] md:h-[75vh] overflow-hidden bg-[#1a1a1a]">
  <img
  src={pageData.img}
  alt="Men's Collection"
  className="w-full h-full object-cover "
/>
  <div className="absolute inset-0 bg-black/45" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10 text-center px-4 sm:px-6">
            <p className="font-['DM_Mono'] text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.35em] uppercase text-[#C7AB5F] mb-3 sm:mb-4">The Collection</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-7xl lg:text-8xl italic leading-none">Men</h1>
            <p className="mt-4 sm:mt-5 text-[11px] sm:text-[12px] uppercase tracking-[0.18em] sm:tracking-[0.25em] text-white/70 max-w-xs sm:max-w-md leading-relaxed">{pageData.description}</p>
          </div>
        </section>

        {/* Services Slider */}
        <section className="pb-10 sm:pb-14 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="rounded-[28px] bg-gradient-to-r from-slate-50 to-zinc-50 p-6 sm:p-8">
            <div className="relative group">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 h-[220px] sm:h-[280px] md:h-[450px]">
                {visibleSlides.map((item, index) => {
                  const shapeClasses = ["shape-full","shape-soft-curve","shape-bottom-curve","shape-top-curve"];
                  return (
                    <div key={`${item.img}-${index}`} className={`relative overflow-hidden shadow-sm ${shapeClasses[index % shapeClasses.length]}`}>
                      <img src={item.img} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                      <p className="absolute bottom-3 left-3 right-3 text-white text-xs sm:text-sm">{item.title}</p>
                    </div>
                  );
                })}
              </div>
              <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition bg-white/90 p-2.5 rounded-full"><ChevronLeft size={20} /></button>
              <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition bg-white/90 p-2.5 rounded-full"><ChevronRight size={20} /></button>
            </div>
          </div>
        </section>

        {/* ── Fabric Library — data from DB (category = "men") ── */}
        <section id="fabric-library" className="pt-2 sm:pt-6 py-12 sm:py-16 md:py-20 px-4 sm:px-6 max-w-7xl mx-auto" >
          <div className="text-center mb-10">
            <p className="font-['DM_Mono'] text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.35em] uppercase text-[#C7AB5F] mb-3">
              Premium Materials
            </p>
            <h2 className="font-['Cormorant_Garamond'] italic text-3xl sm:text-4xl md:text-6xl text-zinc-900">
              Fabric Library
            </h2>
          </div>

          {/* Loading state */}
          {fabricsLoading && (
            <>
              <FabricCarouselSkeleton />
              <FabricCarouselSkeleton />
            </>
          )}

          {/* Error state */}
          {!fabricsLoading && fabricsError && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
              <p className="text-sm text-red-500 font-medium">⚠ {fabricsError}</p>
              <button
                onClick={refetchFabrics}
                className="font-['DM_Mono'] text-[11px] tracking-[0.18em] uppercase border border-[#C7AB5F] text-[#C7AB5F] px-6 py-3 rounded-full hover:bg-[#C7AB5F] hover:text-white transition"
              >
                Retry
              </button>
            </div>
          )}

          {/* Carousels */}
          {!fabricsLoading && !fabricsError && (
            <>
              {fabrics.length > 0 && (
                <FabricCarousel fabricList={fabrics} onOpen={setFabricPopup} />
              )}
              {fabrics1.length > 0 && (
                <FabricCarousel fabricList={fabrics1} onOpen={setFabricPopup} />
              )}
              {fabrics.length === 0 && fabrics1.length === 0 && (
                <p className="text-center text-neutral-400 text-sm py-10">
                  No fabrics available at the moment.
                </p>
              )}
            </>
          )}
        </section>

        {/* Collection Details */}
        <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="bg-[#f8f5ef] rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-12 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 sm:mb-10">
              <div>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.3em] text-[#C7AB5F] mb-2">Collection Details</p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif italic text-zinc-900">Men&apos;s Collection</h2>
              </div>
              <button onClick={() => setShowSizeGuide(true)} className="w-full sm:w-auto font-['DM_Mono'] text-[12px] sm:text-[13px] tracking-[0.12em] sm:tracking-[0.15em] uppercase flex items-center justify-center gap-2 text-[#C7AB5F] border border-[#C7AB5F] px-5 sm:px-6 py-3 rounded-full hover:bg-[#C7AB5F] hover:text-white transition-all duration-200 self-start md:self-auto">
                <Ruler size={16} /> Detailed Size Guide
              </button>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:gap-8 max-w-2xl mx-auto w-full">
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm">
                <h4 className="text-lg sm:text-xl font-semibold text-zinc-900 mb-4">Customization</h4>
                <ul className="space-y-3 text-sm sm:text-base text-zinc-700">
                  {pageData.customization.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[#C7AB5F] mt-1" aria-hidden>●</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="pb-16 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="rounded-[28px] bg-white p-6 sm:p-8 shadow-sm">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl sm:text-4xl font-semibold text-zinc-900">Related Services</h2>
                <p className="mt-3 italic text-2xl text-zinc-500">Services we offer</p>
              </div>
              <p className="text-zinc-600 text-lg">We produce men&apos;s garments with precision — from tailored formals to festive occasion wear.</p>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {menDressTypes.map((type) => (
                <div key={type.name} className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden">
                    <img src={type.img} alt={type.name} className="w-full h-full object-cover" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-zinc-800 leading-tight">{type.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Pillars */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-[#f8f5ef] to-white">
          <div className="px-4 sm:px-6 max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: <Scissors size={28} />, title: "Bespoke Stitching",   desc: "Every kurta, shirt, and suit tailored to your exact measurements by skilled craftsmen." },
              { icon: <Package size={28} />,  title: "Luxury Packaging",    desc: "Garments delivered in signature boxes with archival tissue, a fabric swatch, and a handwritten note." },
              { icon: <RefreshCw size={28} />,title: "Re-stitch Guarantee", desc: "Custom orders revised at no cost until every seam and silhouette is exactly right." },
              { icon: <Phone size={28} />,    title: "Style Concierge",     desc: "Personal styling and fitting support via WhatsApp — at every stage of your order." },
            ].map((pillar) => (
              <div key={pillar.title} className="group bg-white/70 backdrop-blur-sm rounded-3xl p-8 sm:p-10 text-center shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white/50 hover:border-[#C7AB5F]/30">
                <div className="text-[#C7AB5F] mb-6 w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C7AB5F]/10 to-[#C7AB5F]/5 flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">{pillar.icon}</div>
                <h4 className="font-['Cormorant_Garamond'] italic text-2xl sm:text-3xl text-zinc-900 mb-4 leading-tight">{pillar.title}</h4>
                <p className="text-zinc-600 text-base sm:text-lg leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Garment Care */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <p className="font-['DM_Mono'] text-[11px] sm:text-[13px] tracking-[0.3em] uppercase text-[#C7AB5F] mb-4">Preserve Your Investment</p>
            <h2 className="font-['Cormorant_Garamond'] italic text-4xl sm:text-5xl md:text-7xl text-zinc-900 mb-6 leading-none">Garment Care</h2>
          </div>
          <div className="flex gap-3 justify-center mb-12 sm:mb-16 flex-wrap" role="tablist">
            {(Object.keys(careInstructions) as CareKey[]).map((key) => (
              <button key={key} role="tab" aria-selected={activeCare === key} onClick={() => setActiveCare(key)}
                className={`font-['DM_Mono'] text-[12px] sm:text-[14px] tracking-[0.15em] uppercase px-6 sm:px-8 py-3 sm:py-4 border-2 font-medium transition-all duration-300 hover:scale-105 shadow-lg rounded-2xl ${activeCare === key ? "bg-[#1a1a1a] text-white border-[#1a1a1a] shadow-[#1a1a1a]/25" : "text-zinc-700 border-zinc-300 hover:border-[#C7AB5F] hover:text-[#C7AB5F] hover:shadow-[#C7AB5F]/20 bg-white/70 backdrop-blur-sm"}`}>
                {key}
              </button>
            ))}
          </div>
          <div className="bg-gradient-to-br from-[#f8f5ef] to-[#fdfbf8] rounded-3xl p-8 sm:p-12 md:p-16 max-w-3xl mx-auto shadow-2xl border border-[#C7AB5F]/10">
            <ul className="space-y-6">
              {careInstructions[activeCare].map((step, i) => (
                <li key={i} className="flex items-start gap-6 sm:gap-8 group hover:translate-x-2 transition-all duration-300">
                  <span className="font-['Cormorant_Garamond'] italic text-[#C7AB5F] text-3xl sm:text-4xl font-light w-12 sm:w-16 shrink-0 bg-gradient-to-r from-[#C7AB5F]/10 p-2 rounded-2xl group-hover:scale-110 transition-all duration-300">{i + 1}.</span>
                  <span className="text-lg sm:text-xl text-zinc-700 leading-relaxed font-medium">{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Doorstep Process */}
        <section className="bg-[#fafafa] py-24 px-6">
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-16">
              <span className="text-[11px] font-bold text-[#c8a96e] uppercase tracking-[0.3em]">The Process</span>
              <h2 className="text-4xl md:text-5xl font-serif italic text-[#121826] mt-4">How Doorstep Stitching Works</h2>
              <div className="w-20 h-px bg-[#c8a96e] mx-auto mt-6" />
            </div>
            <ol className="relative space-y-12">
              <div className="hidden md:block absolute left-7 top-7 bottom-7 w-px bg-[#c8a96e]" aria-hidden />
              {doorstepServiceSteps.map((step, index) => (
                <li key={index} className="relative flex gap-8 items-start group">
                  <div className="relative z-10 flex-shrink-0 w-14 h-14 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[#121826] font-bold text-lg group-hover:bg-[#051173] group-hover:text-white transition-all duration-300 shadow-sm">
                    {index + 1}
                  </div>
                  <div className="pt-3">
                    <h3 className="text-xl font-bold text-[#121826] mb-2">{step.title}</h3>
                    <p className="text-neutral-500 leading-relaxed max-w-2xl">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-gradient-to-br from-[#C7AB5F]/5 via-white to-[#C7AB5F]/5 border-y-4 border-[#C7AB5F]/30">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-['DM_Mono'] text-[12px] sm:text-[14px] tracking-[0.3em] uppercase text-[#C7AB5F] mb-6">Begin Your Journey</p>
            <h2 className="font-['Cormorant_Garamond'] italic text-4xl sm:text-5xl md:text-7xl text-zinc-900 mb-8 leading-none">Ready To Be Measured?</h2>
            <p className="text-zinc-600 text-xl sm:text-2xl leading-relaxed mb-12 sm:mb-16 max-w-2xl mx-auto">
              Schedule a complimentary consultation with our style team. We'll guide you through fabrics, cuts, and customization — from the first stitch to the last.
            </p>
            <div className="flex flex-col lg:flex-row gap-6 justify-center items-stretch lg:items-center">
              <button className="w-full lg:w-auto font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase bg-[#1a1a1a] text-white px-10 sm:px-12 py-6 sm:py-8 rounded-3xl shadow-2xl transition-all duration-300 font-semibold hover:bg-[#C7AB5F] hover:scale-105" onClick={() => router.push("/form")}>
                Book a Consultation
              </button>
              <button className="w-full lg:w-auto font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase border-2 border-[#1a1a1a] text-zinc-900 px-10 sm:px-12 py-6 sm:py-8 rounded-3xl transition-all duration-300 font-semibold bg-white/70 backdrop-blur-sm shadow-lg hover:bg-[#C7AB5F] hover:border-[#C7AB5F] hover:text-white hover:shadow-xl hover:scale-105">
                WhatsApp Us
              </button>
            </div>
          </div>
        </section>

      </main>

      {/* ── SIZE GUIDE PANEL ── */}
      {showSizeGuide && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 backdrop-blur-[8px]" onClick={() => setShowSizeGuide(false)} aria-hidden />
          <aside role="dialog" aria-modal="true" aria-label="Men's Size Guide"
            className="anim-slide-in fixed top-0 right-0 h-full w-full sm:w-[95%] md:w-4/5 lg:w-2/3 xl:w-1/2 bg-gradient-to-b from-[#fdfbf8] to-[#f8f5ef] z-50 overflow-y-auto shadow-2xl border-l-4 border-[#C7AB5F]/30"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 sm:px-8 py-6 bg-white/80 backdrop-blur-sm border-b border-[#ede8e0]/50">
              <div>
                <p className="font-['DM_Mono'] text-[12px] sm:text-[14px] tracking-[0.25em] uppercase text-[#C7AB5F] mb-2">Modish Kreations</p>
                <h2 className="font-['Cormorant_Garamond'] italic font-light text-2xl sm:text-3xl text-zinc-900">Men&apos;s Size Guide</h2>
              </div>
              <button onClick={() => setShowSizeGuide(false)} className="p-3 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-all duration-200 rounded-xl shadow-sm hover:shadow-md shrink-0" aria-label="Close size guide">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 sm:p-8 md:p-12">
              <div className="flex gap-3 mb-10 sm:mb-12 flex-wrap" role="tablist">
                {(["tops", "bottoms"] as SizeTab[]).map((t) => (
                  <button key={t} role="tab" aria-selected={sizeTab === t} onClick={() => setSizeTab(t)}
                    className={`font-['DM_Mono'] text-[13px] sm:text-[15px] tracking-[0.15em] uppercase px-6 sm:px-8 py-4 border-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 rounded-2xl ${sizeTab === t ? "bg-[#1a1a1a] text-white border-[#1a1a1a] shadow-[#1a1a1a]/25" : "text-zinc-700 border-zinc-200 hover:border-[#C7AB5F] hover:text-[#C7AB5F] bg-white/70 backdrop-blur-sm"}`}>
                    {t === "tops" ? "Tops & Shirts" : "Bottoms & Trousers"}
                  </button>
                ))}
              </div>
              {sizeTab === "tops" && (
                <>
                  <p className="font-['DM_Mono'] text-[13px] sm:text-[15px] tracking-[0.2em] uppercase text-zinc-500 mb-8 font-medium">Men Tops (inches)</p>
                  <div className="overflow-x-auto rounded-3xl shadow-xl border border-zinc-100">
                    <table className="w-full min-w-[500px] text-left border-collapse">
                      <thead className="bg-gradient-to-r from-zinc-50 to-zinc-100">
                        <tr className="border-b-4 border-[#1a1a1a]/10">
                          {["Size","Chest","Shoulder","Sleeve"].map((h) => (
                            <th key={h} className="font-['DM_Mono'] text-[14px] sm:text-[16px] tracking-[0.15em] uppercase text-zinc-800 font-semibold py-6 px-8">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageData.SIZE_GUIDE_TOPS.map((row, i) => (
                          <tr key={row.label} className={`border-b-2 border-zinc-100/50 hover:bg-[#faf7f2] transition-colors duration-200 ${i % 2 === 0 ? "bg-white/80" : "bg-[#fdfbfa]"}`}>
                            <td className="font-['Cormorant_Garamond'] italic text-2xl sm:text-3xl text-[#C7AB5F] py-8 px-8 font-semibold">{row.label}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.chest}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.shoulder}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.sleeve}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {sizeTab === "bottoms" && (
                <>
                  <p className="font-['DM_Mono'] text-[13px] sm:text-[15px] tracking-[0.2em] uppercase text-zinc-500 mb-8 font-medium">Men Bottoms (inches)</p>
                  <div className="overflow-x-auto rounded-3xl shadow-xl border border-zinc-100">
                    <table className="w-full min-w-[500px] text-left border-collapse">
                      <thead className="bg-gradient-to-r from-zinc-50 to-zinc-100">
                        <tr className="border-b-4 border-[#1a1a1a]/10">
                          {["Size","Waist","Hip","Inseam"].map((h) => (
                            <th key={h} className="font-['DM_Mono'] text-[14px] sm:text-[16px] tracking-[0.15em] uppercase text-zinc-800 font-semibold py-6 px-8">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageData.SIZE_GUIDE_BOTTOMS.map((row, i) => (
                          <tr key={row.label} className={`border-b-2 border-zinc-100/50 hover:bg-[#faf7f2] transition-colors duration-200 ${i % 2 === 0 ? "bg-white/80" : "bg-[#fdfbfa]"}`}>
                            <td className="font-['Cormorant_Garamond'] italic text-2xl sm:text-3xl text-[#C7AB5F] py-8 px-8 font-semibold">{row.label}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.waist}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.hip}</td>
                            <td className="font-['DM_Mono'] text-[15px] sm:text-[17px] text-zinc-700 font-semibold py-8 px-8">{row.inseam}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <button onClick={() => setShowSizeGuide(false)} className="mt-12 w-full font-['DM_Mono'] text-[15px] sm:text-[17px] tracking-[0.18em] uppercase bg-gradient-to-r from-[#1a1a1a] to-zinc-900 text-white py-8 rounded-3xl hover:shadow-2xl hover:shadow-[#1a1a1a]/30 hover:scale-105 transition-all duration-300 font-semibold shadow-xl">
                Got it — close guide
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ── FABRIC DETAIL POPUP ── */}
      {fabricPopup && (() => {
        const fabricImages = [fabricPopup.img, fabricPopup.img1, fabricPopup.img2, fabricPopup.img3, fabricPopup.img4].filter(Boolean);
        return (
          <>
            <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setFabricPopup(null)} />
            <div className="fixed inset-0 z-[60] overflow-y-auto">
              <div className="flex min-h-full items-start lg:items-center justify-center p-0 sm:p-4 lg:p-6">
                <div className="relative flex flex-col w-full lg:max-w-[1100px] bg-[#f8f6f2] rounded-none sm:rounded-[22px] overflow-hidden shadow-2xl border-0 sm:border border-zinc-200 sm:my-4 lg:my-auto">
                  <button onClick={() => setFabricPopup(null)}
                    className="absolute top-3 right-3 lg:top-5 lg:right-5 z-50 flex items-center justify-center w-10 h-10 rounded-full border border-zinc-300 bg-white hover:bg-zinc-100 transition-all duration-300">
                    <X size={18} className="text-black" />
                  </button>
                  <div className="flex flex-col lg:flex-row w-full">
                    {/* Images */}
                    <div className="flex flex-col lg:flex-row gap-3 p-3 sm:p-4 lg:p-5 bg-white border-b lg:border-b-0 lg:border-r border-zinc-200 flex-1">
                      <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto scrollbar-hide pb-1 lg:pb-0 lg:pr-1 lg:max-h-[650px] shrink-0">
                        {fabricImages.map((image, index) => (
                          <button key={index} onClick={() => setSelectedImage(image)}
                            className={`flex items-center justify-center min-w-[60px] w-[60px] h-[60px] sm:min-w-[70px] sm:w-[70px] sm:h-[70px] rounded-xl overflow-hidden border-2 transition-all duration-300 shrink-0 ${selectedImage === image ? "border-[#C7AB5F]" : "border-zinc-200 hover:border-zinc-400"}`}>
                            <img src={image} alt={`thumb-${index}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-1 items-center justify-center rounded-[18px] overflow-hidden bg-[#f4f4f4] min-h-[300px] sm:min-h-[420px] lg:min-h-[650px]">
                        <img src={selectedImage || fabricPopup.img} alt={fabricPopup.name} className="flex w-full h-full object-cover object-center" />
                      </div>
                    </div>
                    {/* Info */}
                    <div className="flex flex-col flex-1 px-4 sm:px-5 lg:px-7 py-5 lg:py-7 lg:max-h-[90vh] lg:overflow-y-auto">
                      <p className="font-['DM_Mono'] text-[10px] tracking-[0.28em] uppercase text-[#C7AB5F] mb-2">Fabrics</p>
                      <h2 className="font-['Cormorant_Garamond'] italic text-[1.8rem] sm:text-[2.2rem] lg:text-[2.8rem] leading-none text-zinc-900 mb-4 pr-10 flex">
                        {fabricPopup.name}
                      </h2>
                      {fabricPopup.tag && (
                        <div className="flex mb-5">
                          <span className="bg-[#C7AB5F] text-white text-[10px] tracking-[0.18em] uppercase px-4 py-2 rounded-md font-['DM_Mono'] inline-flex items-center justify-center">
                            {fabricPopup.tag}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col border-y border-zinc-200 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase text-zinc-500 shrink-0">Origin</span>
                          <span className="text-zinc-900 text-[13px] sm:text-[14px] font-medium text-right flex">{fabricPopup.origin}</span>
                        </div>
                      </div>
                      <div className="flex flex-col py-4 border-b border-zinc-200">
                        <p className="text-zinc-600 leading-[1.8] text-[13px] sm:text-[14px] flex">
                          {fabricPopup.description || "Crafted using premium quality fabric with luxury finishing, ideal for modern tailoring and sophisticated custom garments."}
                        </p>
                      </div>
                      <div className="flex flex-col py-4 border-b border-zinc-200">
                        <p className="font-['DM_Mono'] text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Best For</p>
                        <div className="flex flex-wrap gap-2">
                          {fabricPopup.best.map((item: string, index: number) => (
                            <span key={index} className="flex items-center justify-center px-3 py-1.5 rounded-full border border-zinc-300 text-[11px] sm:text-[12px] text-zinc-700 bg-white whitespace-nowrap">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                        <button
                          onClick={() => {
                            setFabricPopup(null);
                            setAppointmentFabric(fabricPopup);
                          }}
                          className="bg-[#051173] hover:bg-[#C7AB5F] text-white py-3.5 rounded-xl transition-all duration-300 font-['DM_Mono'] text-[11px] tracking-[0.18em] uppercase"
                        >
                          Book Appointment
                        </button>
                      </div>
                      <div className="flex gap-2 sm:gap-3 mt-4 mb-2">
                        {["Premium", "Tailored", "Luxury"].map((label) => (
                          <div key={label} className="flex flex-1 items-center justify-center border border-zinc-200 rounded-xl py-3 text-center bg-white">
                            <p className="font-['DM_Mono'] text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-black">{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── FABRIC APPOINTMENT POPUP ── */}
      {appointmentFabric && (
        <FabricAppointmentPopup
          fabric={appointmentFabric}
          onClose={() => setAppointmentFabric(null)}
        />
      )}
    </>
  );
}